import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import CarIcon from "../../components/CarIcon";
import Icon from "../../components/Icon";
import { useActiveCar } from "../../auth/useActiveCar";
import { useSetActiveCar } from "../../api/cars";
import { relativeDate } from "../../lib/date";
import { formatMileage } from "../../lib/odometer";

interface OverviewCarHeaderProps {
  lastOdometerDatetime: string | null;
}

export default function OverviewCarHeader({ lastOdometerDatetime }: OverviewCarHeaderProps) {
  const { cars, activeCar, hasCars, isLoading } = useActiveCar();
  const setActive = useSetActiveCar();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  if (isLoading || !hasCars || !activeCar) return null;

  function handlePick(id: number) {
    if (id !== activeCar?.id) setActive.mutate(id);
    setOpen(false);
  }

  const modelLabel = [activeCar.manufacturer, activeCar.model].filter(Boolean).join(" ");
  const hasOdometer = activeCar.current_odometer_km != null;

  return (
    <div ref={containerRef} className="overview-car-header">
      <button
        type="button"
        className="overview-car-header__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="overview-car-header__icon">
          {activeCar.has_image ? (
            <img
              src={`/api/cars/${activeCar.id}/image`}
              alt=""
              className="overview-car-header__image"
            />
          ) : (
            <CarIcon name={activeCar.icon} size={36} />
          )}
        </span>

        <span className="overview-car-header__body">
          <span className="overview-car-header__name">{activeCar.name}</span>
          {modelLabel && (
            <span className="overview-car-header__sub">{modelLabel}</span>
          )}
          {hasOdometer && (
            <span className="overview-car-header__odometer">
              {formatMileage(activeCar.current_odometer_km)} km
              {lastOdometerDatetime && (
                <> · {relativeDate(lastOdometerDatetime)}</>
              )}
            </span>
          )}
        </span>

        <Icon name="chevron" className="overview-car-header__chevron" />
      </button>

      {open && (
        <div className="car-switcher__popover" role="listbox">
          <ul className="car-switcher__list">
            {cars.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={c.id === activeCar.id}
                  className={
                    "car-switcher__option" +
                    (c.id === activeCar.id ? " car-switcher__option--active" : "")
                  }
                  onClick={() => handlePick(c.id)}
                >
                  <span className="car-switcher__icon">
                    {c.has_image ? (
                      <img
                        src={`/api/cars/${c.id}/image`}
                        alt=""
                        className="car-switcher__image"
                      />
                    ) : (
                      <CarIcon name={c.icon} size={24} />
                    )}
                  </span>
                  <span className="car-switcher__option-body">
                    <span className="car-switcher__option-name">{c.name}</span>
                    {c.access_level !== "owner" && (
                      <span className="car-switcher__option-sub">
                        Shared by {c.owner_username}
                      </span>
                    )}
                  </span>
                  {c.id === activeCar.id && (
                    <Icon name="check" className="car-switcher__check" />
                  )}
                </button>
              </li>
            ))}
          </ul>
          <div className="car-switcher__footer">
            <Link
              to="/garage"
              className="car-switcher__manage"
              onClick={() => setOpen(false)}
            >
              Manage in Garage →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
